import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  signal,
  effect,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComprehensiveVideoStatus } from '@solverml/api';

@Component({
  selector: 'app-video-status-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './video-status-card.component.html',
  styleUrls: ['./video-status-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoStatusCardComponent implements OnInit, OnDestroy {
  private static readonly DISPLAY_TIME_ZONE = 'Europe/Madrid';
  private static readonly DISPLAY_LOCALE = 'es-ES';

  private static readonly STAGE_LABELS: Record<string, string> = {
    INIT: 'Inicio',
    YOUTUBE_DOWNLOAD: 'Descarga de YouTube',
    UPLOAD: 'Subida',
    CLOUD_UPLOADED: 'Subida completada',
    TRANSCODING: 'Transcodificación',
    IA_QUEUED: 'En cola de IA',
    IA_ANALYSIS: 'Preparación de análisis IA',
    IA_PROCESSING: 'Análisis IA en procesamiento',
    IA_COMPLETED: 'Análisis IA completado',
    RELAUNCH_IA_ANALYSIS: 'Relanzamiento de análisis IA',
    READY: 'Listo',
  };

  status = input.required<ComprehensiveVideoStatus>();

  retryClicked = output<string>();
  archiveClicked = output<string>();
  prioritizeClicked = output<string>();
  cardClicked = output<string>();

  canRetry = input<boolean>(false);
  showProcessingStages = input<boolean>(false);
  isTimelineExpanded = signal(false);
  selectedRunKey = signal<string | null>(null);

  private now = signal<number>(Date.now());
  private elapsedTickerId: ReturnType<typeof setInterval> | null = null;

  progressBarWidth = computed(() => {
    const s = this.status();

    if (s.ia_progress_percentage !== null && s.ia_progress_percentage !== undefined) {
      return `${s.ia_progress_percentage}%`;
    }

    if (s.current_status_type === 'COMPLETED') return '100%';
    if (s.current_status_type === 'PENDING') return '5%';
    return '100%';
  });

  isIndeterminate = computed(() => {
    const s = this.status();
    return s.current_status_type === 'PROCESSING' && (s.ia_progress_percentage === null || s.ia_progress_percentage === undefined);
  });

  statusLabel = computed(() => {
    const s = this.status();
    if (this.isQueued() && s.queue_position) {
      const queueType = (s as any).queue_type === 'priority' ? 'prioridad' : 'normal';
      return `En cola ${queueType} (posición ${s.queue_position})`;
    }

    const rawLabel = (s.current_label || '').trim();
    if (!rawLabel) {
      return this.getFriendlyStageLabel(s.current_stage);
    }

    if (this.isTechnicalCode(rawLabel)) {
      return this.getFriendlyStageLabel(rawLabel);
    }

    return rawLabel;
  });

  currentStatusBadge = computed(() => {
    const s = this.status();
    if (s.current_status_type === 'ERROR' || s.is_error) {
      return { label: 'Error', className: 'badge--error' };
    }

    if (s.current_stage === 'IA_QUEUED' || s.current_status_type === 'PENDING') {
      return { label: 'En cola', className: 'badge--queued' };
    }

    if (s.current_status_type === 'COMPLETED') {
      return { label: 'Completado', className: 'badge--completed' };
    }

    if (s.current_status_type === 'PROCESSING') {
      return { label: 'Procesando', className: 'badge--processing' };
    }

    return { label: 'Pendiente', className: 'badge--pending' };
  });

  lastStatusTimestamp = computed(() => this.status().history_log.at(-1)?.timestamp ?? null);

  isQueued = computed(() => {
    const s = this.status();
    return s.current_stage === 'IA_QUEUED' || s.current_status_type === 'PENDING';
  });

  queueTypeLabel = computed(() => {
    const queueType = (this.status() as any).queue_type as string | undefined;
    if (!queueType) return 'normal';
    return queueType === 'priority' ? 'prioridad' : 'normal';
  });

  queuePositionLabel = computed(() => {
    const queueType = (this.status() as any).queue_type as string | undefined;
    if (queueType === 'priority') return 'Posición cola prioridad';
    return 'Posición cola normal';
  });

  globalQueuePosition = computed(() => {
    if (!this.isQueued()) return null;

    const raw = (this.status() as any).global_queue_position as number | undefined;
    if (raw === undefined || raw === null) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;

    const queueType = (this.status() as any).queue_type as string | undefined;
    const localQueuePosition = Number(this.status().queue_position ?? 0);

    // Si solo existe cola normal, la posición global coincide con la local y aporta ruido.
    if (queueType !== 'priority' && localQueuePosition > 0 && parsed === localQueuePosition) {
      return null;
    }

    return parsed;
  });

  isReprocess = computed(() => Boolean((this.status() as any).is_reprocess));

  etaRangeText = computed(() => {
    const etaMin = (this.status() as any).eta_min_seconds as number | undefined;
    const etaMax = (this.status() as any).eta_max_seconds as number | undefined;

    if (etaMin === undefined || etaMin === null || etaMax === undefined || etaMax === null) {
      return null;
    }

    return `${this.formatDurationCompact(etaMin)} - ${this.formatDurationCompact(etaMax)}`;
  });

  etaLabel = computed(() => {
    if (!this.etaRangeText()) return null;
    return this.isQueued() ? 'ETA entrada a procesamiento' : 'ETA finalización';
  });

  timeElapsedText = computed(() => {
    const createdAtMs = new Date(this.status().created_at).getTime();

    if (!Number.isFinite(createdAtMs)) {
      const fallbackMinutes = this.status().elapsed_minutes;
      if (fallbackMinutes === null || fallbackMinutes === undefined) return '';
      const totalSeconds = Math.max(0, fallbackMinutes * 60);
      return this.formatElapsedTime(totalSeconds);
    }

    const totalSeconds = Math.max(0, Math.floor((this.now() - createdAtMs) / 1000));
    return this.formatElapsedTime(totalSeconds);
  });

  statusClass = computed(() => {
    const type = this.status().current_status_type;
    if (this.status().current_stage === 'IA_QUEUED') {
      return 'card--queued';
    }

    switch (type) {
      case 'ERROR': return 'card--error';
      case 'COMPLETED': return 'card--completed';
      case 'PROCESSING': return 'card--processing';
      default: return 'card--pending';
    }
  });

  iconName = computed(() => {
    const type = this.status().current_status_type;
    if (this.status().current_stage === 'IA_QUEUED') {
      return 'schedule_send';
    }

    switch (type) {
      case 'ERROR': return 'error_outline';
      case 'COMPLETED': return 'check_circle_outline';
      case 'PROCESSING': return 'settings_suggest';
      default: return 'hourglass_empty';
    }
  });

  processingRuns = computed(() => {
    const history = this.status().history_log ?? [];
    const processingIndices: number[] = [];
    for (let i = 0; i < history.length; i++) {
      if (history[i]?.stage === 'IA_PROCESSING') {
        processingIndices.push(i);
      }
    }
    if (processingIndices.length === 0) return [] as Array<{
      key: string;
      label: string;
      stages: any[];
      startedAt: string | null;
      startedAtMs: number | null;
      isReprocess: boolean;
    }>;

    const runStarts: number[] = [];
    let lastRunStart = processingIndices[0];
    runStarts.push(lastRunStart);

    for (let i = 1; i < processingIndices.length; i++) {
      const currentIdx = processingIndices[i];
      const prevIdx = processingIndices[i - 1];
      const hasRelaunchBetween = history
        .slice(prevIdx + 1, currentIdx + 1)
        .some((event) => event?.stage === 'RELAUNCH_IA_ANALYSIS');

      if (hasRelaunchBetween) {
        lastRunStart = currentIdx;
        runStarts.push(lastRunStart);
      }
    }

    const runs = runStarts.map((startIdx, runIdx) => {
      const endIdx = runIdx + 1 < runStarts.length ? runStarts[runIdx + 1] : history.length;
      const runEvents = history.slice(startIdx, endIdx);
      const latestWithStages = [...runEvents]
        .reverse()
        .find((event) => Array.isArray((event.metadata as any)?.stages));
      const stages = ((latestWithStages?.metadata as any)?.stages ?? []) as any[];
      const displayStages = Array.isArray(stages)
        ? [...stages]
          .map((stage) => {
            const subs = Array.isArray(stage?.sub_stages) ? [...stage.sub_stages].reverse() : stage?.sub_stages;
            return { ...stage, sub_stages: subs };
          })
          .reverse()
        : [];
      const startedAt = runEvents[0]?.timestamp ?? null;
      const startedAtMs = this.parseDate(startedAt)?.getTime() ?? null;
      const isReprocess = runEvents.some((event) => {
        const meta = (event as any)?.metadata;
        return Boolean(meta && typeof meta === 'object' && (meta as any).reprocessing === true);
      });
      const key = `${startIdx}-${runIdx}`;
      const label = runStarts.length === 1
        ? 'Procesamiento actual'
        : `Procesamiento ${runIdx + 1}`;

      return {
        key,
        label,
        stages: displayStages,
        startedAt: startedAt ? String(startedAt) : null,
        startedAtMs,
        isReprocess,
      };
    });

    return [...runs].reverse().map((run, idx) => {
      if (idx === 0) return { ...run, label: runStarts.length > 1 ? 'Procesamiento más reciente' : run.label };
      return run;
    });
  });

  selectedProcessingRun = computed(() => {
    const runs = this.processingRuns();
    const selected = this.selectedRunKey();
    if (!runs.length) return null;
    if (!selected) return runs[0];
    return runs.find((run) => run.key === selected) ?? runs[0];
  });

  stageTree = computed(() => this.selectedProcessingRun()?.stages ?? []);

  constructor() {
    effect(
      () => {
        const runs = this.processingRuns();
        const selected = this.selectedRunKey();
        if (!runs.length) {
          if (selected !== null) {
            this.selectedRunKey.set(null);
          }
          return;
        }
        if (!selected || !runs.some((run) => run.key === selected)) {
          this.selectedRunKey.set(runs[0].key);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.elapsedTickerId = setInterval(() => {
      this.now.set(Date.now());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.elapsedTickerId) {
      clearInterval(this.elapsedTickerId);
      this.elapsedTickerId = null;
    }
  }

  toggleTimeline(event: Event): void {
    event.stopPropagation();
    this.isTimelineExpanded.update(v => !v);
  }

  onRetry(event: Event): void {
    event.stopPropagation();
    this.retryClicked.emit(this.status().video_id);
  }

  onArchive(event: Event): void {
    event.stopPropagation();
    this.archiveClicked.emit(this.status().video_id);
  }

  onPrioritize(event: Event): void {
    event.stopPropagation();
    this.prioritizeClicked.emit(this.status().video_id);
  }

  onClick(): void {
    this.cardClicked.emit(this.status().video_id);
  }

  selectProcessingRun(runKey: string, event: Event): void {
    event.stopPropagation();
    this.selectedRunKey.set(runKey);
  }

  selectRunFromTimeline(event: Event, timelineEvent: any): void {
    if (!this.showProcessingStages()) return;
    const runKey = this.getRunKeyForTimelineEvent(timelineEvent);
    if (!runKey) return;
    event.stopPropagation();
    this.selectedRunKey.set(runKey);
  }

  isTimelineRunSelectable(timelineEvent: any): boolean {
    if (!this.showProcessingStages()) return false;
    return this.getRunKeyForTimelineEvent(timelineEvent) !== null;
  }

  isTimelineRunSelected(timelineEvent: any): boolean {
    const runKey = this.getRunKeyForTimelineEvent(timelineEvent);
    return !!runKey && runKey === this.selectedProcessingRun()?.key;
  }

  selectedRunTypeLabel(): string | null {
    const run = this.selectedProcessingRun();
    if (!run) return null;
    return run.isReprocess ? 'Reprocesado' : 'Procesamiento normal';
  }

  getTimelineRunLinkLabel(timelineEvent: any): string {
    const runKey = this.getRunKeyForTimelineEvent(timelineEvent);
    if (!runKey) return 'Ver stages de este procesamiento';
    const run = this.processingRuns().find((item) => item.key === runKey);
    if (run?.isReprocess) return 'Ver stages de este reprocesado';
    return 'Ver stages de este procesamiento';
  }

  formatTimelineStage(rawStage: string): string {
    return this.getFriendlyStageLabel(rawStage);
  }

  formatTimelineLabel(rawLabel: string): string {
    const normalized = (rawLabel || '').trim();
    if (!normalized) return 'Sin detalle';
    if (this.isTechnicalCode(normalized)) {
      return this.getFriendlyStageLabel(normalized);
    }
    return normalized;
  }

  private formatElapsedTime(totalSeconds: number): string {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) {
      return `Hace ${days}d ${hours}h ${minutes}m ${seconds}s`;
    }

    return `Hace ${hours}h ${minutes}m ${seconds}s`;
  }

  private formatDurationCompact(totalSeconds: number): string {
    const safe = Math.max(0, Math.round(totalSeconds));
    if (safe === 0) {
      return '0m';
    }
    if (safe < 60) {
      return '<1m';
    }

    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  formatStageDuration(entity: any): string | null {
    if (!entity || typeof entity !== 'object') return null;

    const rawDuration = Number(entity.duration_seconds);
    if (Number.isFinite(rawDuration) && rawDuration > 0) {
      return this.formatDurationDetailed(rawDuration);
    }
    if (Number.isFinite(rawDuration) && rawDuration <= 0) {
      return '> 1s';
    }

    const startedAt = this.parseDate(entity.started_at);
    const completedAt = this.parseDate(entity.completed_at);
    if (!startedAt || !completedAt) return null;

    const seconds = Math.max(0, Math.round((completedAt.getTime() - startedAt.getTime()) / 1000));
    if (seconds <= 0) return '> 1s';
    return this.formatDurationDetailed(seconds);
  }

  private parseDate(value: unknown): Date | null {
    if (!value || typeof value !== 'string') return null;
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed;
  }

  private getRunKeyForTimelineEvent(timelineEvent: any): string | null {
    if (!timelineEvent || typeof timelineEvent !== 'object') return null;
    const stage = String(timelineEvent.stage || '');
    if (stage !== 'IA_PROCESSING') return null;

    const eventMs = this.parseDate(timelineEvent.timestamp)?.getTime();
    if (!Number.isFinite(eventMs)) return null;

    const runs = this.processingRuns();
    if (!runs.length) return null;

    const direct = runs.find((run) => Number.isFinite(run.startedAtMs) && run.startedAtMs === eventMs);
    return direct?.key ?? null;
  }

  private formatDurationDetailed(totalSeconds: number): string {
    const safe = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;

    if (hours > 0) {
      if (seconds > 0) return `${hours}h ${minutes}m ${seconds}s`;
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      if (seconds > 0) return `${minutes}m ${seconds}s`;
      return `${minutes}m`;
    }
    return `${seconds}s`;
  }

  private getFriendlyStageLabel(rawStage: string | null | undefined): string {
    const normalized = (rawStage || '').trim();
    if (!normalized) return 'Sin estado';

    const mapped = VideoStatusCardComponent.STAGE_LABELS[normalized];
    if (mapped) return mapped;

    if (this.isTechnicalCode(normalized)) {
      return normalized
        .split('_')
        .filter(Boolean)
        .map((part) => part.toLowerCase())
        .join(' ');
    }

    return normalized;
  }

  private isTechnicalCode(value: string): boolean {
    return /^[A-Z0-9_]+$/.test(value);
  }

  formatDateMadrid(value: string | Date | null | undefined, withYear = false, withSeconds = false): string {
    if (!value) return '--';
    const date = value instanceof Date ? value : new Date(value);
    if (!Number.isFinite(date.getTime())) return '--';

    const options: Intl.DateTimeFormatOptions = withYear
      ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

    if (withSeconds) {
      options.second = '2-digit';
    }

    const formatter = new Intl.DateTimeFormat(
      VideoStatusCardComponent.DISPLAY_LOCALE,
      {
        ...options,
        hour12: false,
        timeZone: VideoStatusCardComponent.DISPLAY_TIME_ZONE
      }
    );
    return formatter.format(date);
  }

  formatStageStatus(status: string | null | undefined): string {
    switch ((status || '').toUpperCase()) {
      case 'INIT':
        return 'Iniciado';
      case 'COMPLETED':
        return 'Completado';
      case 'ERROR':
        return 'Error';
      default:
        return status || 'Desconocido';
    }
  }
}
